/**
 * ActivityLogTab - Admin activity tracking dashboard
 * Shows all admin/staff activities with filtering and role-based access
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Activity, User, Clock, Filter, Search, Shield, Trash2, Edit,
  LogIn, LogOut, ShoppingBag, Coffee, Tag, Users, Settings,
  FileText, Image, Bell, Send, ChevronLeft, ChevronRight, AlertTriangle,
  Gift, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { api } from '@/utils/apiClient';

// Icon mapping for activity types
const ACTIVITY_ICONS = {
  login: LogIn,
  logout: LogOut,
  login_failed: AlertTriangle,
  order_view: ShoppingBag,
  order_status_update: ShoppingBag,
  order_comp: Gift,
  order_uncomp: Gift,
  order_refund: ShoppingBag,
  order_cancel: ShoppingBag,
  order_create_staff: ShoppingBag,
  menu_item_create: Coffee,
  menu_item_update: Coffee,
  menu_item_delete: Coffee,
  menu_item_availability: Coffee,
  inventory_update: Bell,
  contact_create: Users,
  contact_update: Users,
  contact_delete: Users,
  contact_lookup: Search,
  discount_assign: Tag,
  promo_code_create: Tag,
  campaign_update: Send,
  campaign_send: Send,
  ab_test_create: Activity,
  settings_update: Settings,
  content_update: FileText,
  media_upload: Image,
  media_delete: Image,
  user_create: Users,
  user_update: Users,
  user_delete: Users,
  report_export: FileText,
  log_modify: Edit,
  log_delete: Trash2,
  default: Activity
};

const ACTIVITY_COLORS = {
  login: 'text-green-500 bg-green-500/10',
  logout: 'text-gray-500 bg-gray-500/10',
  login_failed: 'text-red-500 bg-red-500/10',
  order_comp: 'text-purple-500 bg-purple-500/10',
  order_uncomp: 'text-orange-500 bg-orange-500/10',
  discount_assign: 'text-blue-500 bg-blue-500/10',
  log_modify: 'text-yellow-500 bg-yellow-500/10',
  log_delete: 'text-red-500 bg-red-500/10',
  default: 'text-primary bg-primary/10'
};

export default function ActivityLogTab({ currentUser }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchUser, setSearchUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterTarget, setFilterTarget] = useState('');
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Selected log for details
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [modifyNotes, setModifyNotes] = useState('');
  const [showModify, setShowModify] = useState(null);
  
  // Activity types
  const [activityTypes, setActivityTypes] = useState({});

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (searchUser) params.user_email = searchUser;
      if (filterAction) params.action_type = filterAction;
      if (filterTarget) params.target_type = filterTarget;
      
      const res = await api.get('/admin/activity-log', { params });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
    setLoading(false);
  }, [page, searchUser, filterAction, filterTarget]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/activity-log/stats/summary', { params: { days: 30 } });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const res = await api.get('/admin/activity-log/types');
      setActivityTypes(res.data.activity_types || {});
    } catch (err) {
      console.error('Failed to fetch activity types');
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchActivityTypes();
  }, [fetchLogs]);

  const handleDelete = async (logId) => {
    try {
      await api.delete(`/admin/activity-log/${logId}`);
      toast.success('Log entry deleted');
      setShowDeleteConfirm(null);
      fetchLogs();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleModify = async (logId) => {
    try {
      await api.put(`/admin/activity-log/${logId}`, { notes: modifyNotes });
      toast.success('Log entry modified');
      setShowModify(null);
      setModifyNotes('');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to modify');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getIcon = (actionType) => {
    return ACTIVITY_ICONS[actionType] || ACTIVITY_ICONS.default;
  };

  const getColor = (actionType) => {
    return ACTIVITY_COLORS[actionType] || ACTIVITY_COLORS.default;
  };

  const isOwner = currentUser?.role === 'owner';

  return (
    <div className="space-y-6" data-testid="activity-log-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Activity Log</h2>
          <p className="text-muted-foreground">Track all admin and staff actions</p>
        </div>
        <Button onClick={() => { fetchLogs(); fetchStats(); }} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Logs (30d)" value={stats.total_logs} icon={Activity} />
          <StatCard label="Last 24 Hours" value={stats.recent_24h} icon={Clock} />
          <StatCard 
            label="Top Action" 
            value={stats.by_action_type?.[0]?.action?.replace(/_/g, ' ') || '-'} 
            icon={Activity}
            subtext={stats.by_action_type?.[0]?.count ? `${stats.by_action_type[0].count} times` : ''}
          />
          <StatCard 
            label="Most Active" 
            value={stats.by_user?.[0]?.user?.split('@')[0] || '-'} 
            icon={User}
            subtext={stats.by_user?.[0]?.count ? `${stats.by_user[0].count} actions` : ''}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-card p-4 rounded-xl border border-border">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters:</span>
        </div>
        
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by user email..."
            value={searchUser}
            onChange={(e) => { setSearchUser(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>
        
        <select
          value={filterAction}
          onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[160px]"
        >
          <option value="">All Actions</option>
          {Object.entries(activityTypes).map(([key, desc]) => (
            <option key={key} value={key}>{key.replace(/_/g, ' ')}</option>
          ))}
        </select>
        
        <select
          value={filterTarget}
          onChange={(e) => { setFilterTarget(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[140px]"
        >
          <option value="">All Targets</option>
          <option value="order">Orders</option>
          <option value="menu_item">Menu Items</option>
          <option value="contact">Contacts</option>
          <option value="campaign">Campaigns</option>
          <option value="settings">Settings</option>
          <option value="activity_log">Logs</option>
        </select>
        
        {(searchUser || filterAction || filterTarget) && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { setSearchUser(''); setFilterAction(''); setFilterTarget(''); setPage(1); }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Log List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{total} total entries</span>
          {isOwner && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Shield className="w-4 h-4" />
              <span>Owner access: Can modify/delete logs</span>
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No activity logs found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {logs.map((log) => {
              const Icon = getIcon(log.action_type);
              const color = getColor(log.action_type);
              
              return (
                <div 
                  key={log.log_id}
                  className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedLog(log)}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">
                          {log.action_description}
                        </span>
                        {log.is_modified && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500/10 text-yellow-600">
                            Modified
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {log.user_email}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded ${
                          log.user_role === 'owner' ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400' :
                          log.user_role === 'admin' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                          'bg-gray-500/10 text-gray-600 dark:text-gray-400'
                        }`}>
                          {log.user_role}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.timestamp)}
                        </span>
                        {log.target_type && (
                          <span className="text-muted-foreground/70">
                            Target: {log.target_type}
                          </span>
                        )}
                      </div>
                    </div>

                    {isOwner && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); setShowModify(log); setModifyNotes(log.modification_notes || ''); }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive"
                          onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(log); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Activity Details</DialogTitle>
            <DialogDescription>
              Full details of this activity log entry
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Action</p>
                  <p className="font-medium">{selectedLog.action_type?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">User</p>
                  <p className="font-medium">{selectedLog.user_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Role</p>
                  <p className="font-medium capitalize">{selectedLog.user_role}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Timestamp</p>
                  <p className="font-medium">{formatDate(selectedLog.timestamp)}</p>
                </div>
                {selectedLog.target_type && (
                  <div>
                    <p className="text-muted-foreground">Target Type</p>
                    <p className="font-medium">{selectedLog.target_type}</p>
                  </div>
                )}
                {selectedLog.target_id && (
                  <div>
                    <p className="text-muted-foreground">Target ID</p>
                    <p className="font-medium font-mono text-xs">{selectedLog.target_id}</p>
                  </div>
                )}
                {selectedLog.ip_address && (
                  <div>
                    <p className="text-muted-foreground">IP Address</p>
                    <p className="font-medium">{selectedLog.ip_address}</p>
                  </div>
                )}
              </div>
              
              <div>
                <p className="text-muted-foreground text-sm mb-1">Description</p>
                <p className="p-3 bg-muted rounded-lg">{selectedLog.action_description}</p>
              </div>
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Details</p>
                  <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedLog.is_modified && (
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <p className="text-sm font-medium text-yellow-600 mb-1">Modified</p>
                  <p className="text-xs text-muted-foreground">
                    By {selectedLog.modified_by} on {formatDate(selectedLog.modified_at)}
                  </p>
                  {selectedLog.modification_notes && (
                    <p className="text-sm mt-1">{selectedLog.modification_notes}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Log Entry</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this log entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-2 mt-4">
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => handleDelete(showDeleteConfirm?.log_id)}
            >
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(null)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modify Modal */}
      <Dialog open={!!showModify} onOpenChange={() => setShowModify(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Notes to Log Entry</DialogTitle>
            <DialogDescription>
              Add modification notes to this log entry (owner only)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Add notes..."
              value={modifyNotes}
              onChange={(e) => setModifyNotes(e.target.value)}
            />
            
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => handleModify(showModify?.log_id)}
              >
                Save Notes
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowModify(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, subtext }) {
  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-lg font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {subtext && <p className="text-xs text-muted-foreground/70">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}
